import type { ProjectOverview } from '../types/project';

/**
 * The project-overview demo dataset - the engineering projects a manager drills
 * into from the command center. They continue the same Payments-Platform story
 * the breaks gallery and command center tell: the recurring playbook violations
 * land on these projects and push their delivery dates around. Breaking
 * commitments map to real breakIds so deep diving opens the same examine view
 * used elsewhere in the demo.
 *
 * Three projects, one of each standing, so the switcher shows real variety:
 * Checkout Revamp (at-risk, recoverable), Fraud Signals v2 (on-track, clean),
 * Ledger Migration (off-track, slipping).
 */

const CHECKOUT_REVAMP_PROJECT: ProjectOverview = {
  id: 'checkout-revamp',
  name: 'Checkout Revamp',
  team: 'Payments Platform',
  portfolio: 'Q4 Payments',
  summary:
    'New hosted checkout with saved cards, retry-safe webhooks, and a migrated orders schema.',
  headline: 'Shipping ~18 days late — but recoverable this week.',
  bottomLines: [
    'The slip is caused by one reopened dependency and an overloaded owner — not the scope.',
    'Three moves regain the Nov 24 date; the team can act on all of them today.',
    'Two procedures are quietly drifting off-book on top of what’s already breaking live.',
  ],
  verdict: 'at-risk',
  targetDate: 'Nov 24',
  projectedDate: 'Dec 12',
  slipLabel: '+18 days',
  confidenceNote:
    'High confidence — three independent signals point the same way.',
  progressPct: 62,
  stats: [
    { value: '62', unit: '%', label: 'scope complete' },
    { value: '+18', unit: 'days', label: 'projected slip', isUrgent: true },
    { value: '3', label: 'commitments breaking', isUrgent: true },
    { value: '4', label: 'procedures off-book' },
    { value: '9', label: 'engineers on it' },
  ],
  timeline: {
    axis: ['Sep', 'Oct', 'Nov', 'Dec'],
    committedPosition: 70,
    projectedPosition: 90,
    milestones: [
      {
        id: 'kickoff',
        label: 'Kickoff',
        dateLabel: 'Sep 8',
        status: 'done',
        position: 4,
      },
      {
        id: 'cards',
        label: 'Saved cards',
        dateLabel: 'Oct 6',
        status: 'done',
        position: 28,
      },
      {
        id: 'webhooks',
        label: 'Retry-safe webhooks',
        dateLabel: 'Oct 30',
        status: 'at-risk',
        position: 48,
      },
      {
        id: 'migration',
        label: 'Orders schema migrated',
        dateLabel: 'Nov 12',
        status: 'active',
        position: 62,
      },
      {
        id: 'launch',
        label: 'Launch',
        dateLabel: 'Nov 24',
        status: 'upcoming',
        position: 70,
      },
    ],
  },
  remediation: {
    headline: 'Three moves regain the Nov 24 target.',
    blurb:
      'The slip traces to one reopened dependency and an overloaded owner, not the scope. Clear both and the date holds.',
    recoversLabel: '+18 days',
    steps: [
      {
        id: 'reblock-dependents',
        title: 'Re-block the 3 dependents of PAY-9281',
        detail:
          'The reopened webhook ticket left three "ready" tickets building on an unverified fix. Re-block them until it is re-verified.',
        impactLabel: 'removes the P1 exposure',
        actionLabel: 'Re-block & notify',
      },
      {
        id: 'rebalance-owner',
        title: 'Rebalance Amelia Ng’s critical-path load',
        detail:
          'One engineer holds 18 points of critical-path work in a 10-point sprint. Move two tickets to free the schedule.',
        impactLabel: 'recovers +7 days',
        actionLabel: 'Rebalance sprint',
      },
      {
        id: 'rollback-gate',
        title: 'Gate the orders migration on a rollback plan',
        detail:
          'The schema migration is queued without a documented rollback. Require one before review to avoid a repeat incident.',
        impactLabel: 'recovers +11 days of risk',
        actionLabel: 'Request changes',
      },
    ],
  },
  procedures: [
    // Off-book, but already surfaced live in "Breaking now" - de-duped out.
    {
      id: 'reblock-dependents-rule',
      name: 'Reopened tickets re-block their dependents',
      summary:
        'When a ticket reopens, its dependents must be re-blocked until it is re-verified.',
      isFollowed: false,
      complianceLabel: '0 of 1 reopens',
      compliancePct: 0,
      trendLabel: 'breaking now',
      severity: 'high',
      relatedBreakId: 'stale-dependency-incident',
    },
    {
      id: 'rollback-rule',
      name: 'Migrations ship with a rollback plan',
      summary:
        'Every migration must carry a documented rollback before it can be reviewed.',
      isFollowed: false,
      complianceLabel: '2 of 5 migrations',
      compliancePct: 40,
      trendLabel: '3rd miss this quarter',
      severity: 'medium',
      relatedBreakId: 'migration-no-rollback',
    },
    // Silent off-book drift - not yet a live incident, so it shows in the column.
    {
      id: 'review-sla-rule',
      name: 'PRs get a first review within 24h',
      summary:
        'Open PRs should reach a first review inside a day to keep the pipeline moving.',
      isFollowed: false,
      complianceLabel: 'avg 41h to first review',
      compliancePct: 46,
      trendLabel: 'slipping 3 sprints',
      severity: 'medium',
    },
    {
      id: 'design-doc-rule',
      name: 'Design doc precedes implementation',
      summary:
        'Non-trivial features start from a short design doc the team has signed off.',
      isFollowed: false,
      complianceLabel: '4 of 6 features',
      compliancePct: 67,
      trendLabel: '2nd skip this month',
      severity: 'low',
    },
    // Followed - held over time.
    {
      id: 'estimate-notify-rule',
      name: 'Re-estimates notify dependent teams same-day',
      summary:
        'Any re-point on the shared pipeline pings dependent teams the same day.',
      isFollowed: true,
      complianceLabel: '6 of 6 re-points',
      compliancePct: 100,
      trendLabel: 'held all sprint',
    },
    {
      id: 'contract-first-rule',
      name: 'Endpoints match the documented API contract',
      summary:
        'Shipped endpoints are validated against the published contract on merge.',
      isFollowed: true,
      complianceLabel: '23 of 23 endpoints',
      compliancePct: 100,
      trendLabel: 'held all quarter',
    },
    {
      id: 'review-coverage-rule',
      name: 'PRs carry tests above the 85% line',
      summary: 'Merged PRs keep test coverage above the team’s 85% floor.',
      isFollowed: true,
      complianceLabel: '88% coverage',
      compliancePct: 88,
      trendLabel: 'above the line all sprint',
    },
    {
      id: 'secret-scan-rule',
      name: 'No secrets reach the repo',
      summary: 'The pre-merge scanner blocks any commit carrying a secret.',
      isFollowed: true,
      complianceLabel: '0 findings',
      compliancePct: 100,
      trendLabel: 'clean all quarter',
    },
  ],
  breakingCommitments: [
    {
      id: 'pay-9281',
      breakId: 'stale-dependency-incident',
      severity: 'high',
      tag: 'Stale dependency',
      ref: 'PAY-9281',
      ageLabel: '3h ago',
      title:
        'A “done” dependency reopened after a P1, dependents not reverified',
      detail:
        'Duplicate charges on retried webhooks reopened PAY-9281. Three tickets still build on the unverified fix.',
      currentMetric: '3 dependents',
      targetMetric: 'all should be re-blocked',
      platforms: ['pagerduty', 'jira'],
      owner: { name: 'Maya Cohen', initials: 'MC' },
    },
    {
      id: 'srch-771',
      breakId: 'engineer-overloaded',
      severity: 'high',
      tag: 'Owner overload',
      ref: 'SRCH-771',
      ageLabel: '3h ago',
      title: 'One engineer owns 4 critical-path tickets this sprint',
      detail:
        '18 points of critical-path work sit with a single owner in a 10-point capacity sprint.',
      currentMetric: '18 pts',
      targetMetric: 'vs 10-pt capacity',
      platforms: ['jira'],
      owner: { name: 'Amelia Ng', initials: 'AN' },
    },
    {
      id: 'pr-4821',
      breakId: 'migration-no-rollback',
      severity: 'medium',
      tag: 'Missing rollback',
      ref: 'PR #4821',
      ageLabel: '5h ago',
      title: 'Orders-schema migration opened with no rollback section',
      detail:
        'The migration is queued for review without a documented rollback — the third miss this quarter.',
      currentMetric: 'no rollback',
      targetMetric: 'required before review',
      platforms: ['github'],
      owner: { name: 'Jordan Doe', initials: 'JD' },
    },
  ],
};

const FRAUD_SIGNALS_PROJECT: ProjectOverview = {
  id: 'fraud-signals-v2',
  name: 'Fraud Signals v2',
  team: 'Risk Platform',
  portfolio: 'Q4 Payments',
  summary:
    'Real-time scoring model with a streaming feature store and shadow-mode rollout.',
  headline: 'On track for Dec 5 — nothing needs a decision.',
  bottomLines: [
    'Every milestone has landed on or ahead of plan; shadow mode is live and clean.',
    'All six procedures are being followed — no off-book work this quarter.',
    'No commitments are breaking; keep the current pace.',
  ],
  verdict: 'on-track',
  targetDate: 'Dec 5',
  projectedDate: 'Dec 5',
  slipLabel: '',
  confidenceNote: 'On pace — no diverging signals across the connected tools.',
  progressPct: 78,
  stats: [
    { value: '78', unit: '%', label: 'scope complete' },
    { value: '0', label: 'days of slip' },
    { value: '0', label: 'commitments breaking' },
    { value: '0', label: 'procedures off-book' },
    { value: '6', label: 'engineers on it' },
  ],
  timeline: {
    axis: ['Sep', 'Oct', 'Nov', 'Dec'],
    committedPosition: 78,
    projectedPosition: 78,
    milestones: [
      {
        id: 'kickoff',
        label: 'Kickoff',
        dateLabel: 'Sep 2',
        status: 'done',
        position: 2,
      },
      {
        id: 'feature-store',
        label: 'Feature store',
        dateLabel: 'Oct 1',
        status: 'done',
        position: 26,
      },
      {
        id: 'model',
        label: 'Scoring model',
        dateLabel: 'Oct 24',
        status: 'done',
        position: 44,
      },
      {
        id: 'shadow',
        label: 'Shadow mode',
        dateLabel: 'Nov 18',
        status: 'active',
        position: 64,
      },
      {
        id: 'ga',
        label: 'GA rollout',
        dateLabel: 'Dec 5',
        status: 'upcoming',
        position: 78,
      },
    ],
  },
  procedures: [
    {
      id: 'fs-shadow-rule',
      name: 'Models ship behind a shadow-mode window',
      summary:
        'Scoring models run in shadow before any traffic is routed to them.',
      isFollowed: true,
      complianceLabel: '14 days in shadow',
      compliancePct: 100,
      trendLabel: 'held every release',
    },
    {
      id: 'fs-rollback-rule',
      name: 'Migrations ship with a rollback plan',
      summary:
        'Every migration carries a documented, tested rollback before review.',
      isFollowed: true,
      complianceLabel: '3 of 3 migrations',
      compliancePct: 100,
      trendLabel: 'held all quarter',
    },
    {
      id: 'fs-contract-rule',
      name: 'Endpoints match the documented API contract',
      summary:
        'The scoring API is validated against the published contract on merge.',
      isFollowed: true,
      complianceLabel: '11 of 11 endpoints',
      compliancePct: 100,
      trendLabel: 'held all quarter',
    },
    {
      id: 'fs-coverage-rule',
      name: 'PRs carry tests above the 85% line',
      summary: 'Merged PRs keep coverage above the team’s 85% floor.',
      isFollowed: true,
      complianceLabel: '91% coverage',
      compliancePct: 91,
      trendLabel: 'above the line all sprint',
    },
    {
      id: 'fs-notify-rule',
      name: 'Re-estimates notify dependent teams same-day',
      summary: 'Re-points on shared work ping dependent teams the same day.',
      isFollowed: true,
      complianceLabel: '1 of 1 re-points',
      compliancePct: 100,
      trendLabel: 'held this sprint',
    },
    {
      id: 'fs-secret-rule',
      name: 'No secrets reach the repo',
      summary: 'The pre-merge scanner blocks any commit carrying a secret.',
      isFollowed: true,
      complianceLabel: '0 findings',
      compliancePct: 100,
      trendLabel: 'clean all quarter',
    },
  ],
  breakingCommitments: [],
};

const LEDGER_MIGRATION_PROJECT: ProjectOverview = {
  id: 'ledger-migration',
  name: 'Ledger Migration',
  team: 'Core Banking',
  portfolio: 'Q4 Payments',
  summary:
    'Move the double-entry ledger off the legacy monolith onto the new event-sourced service.',
  headline:
    'Will miss Dec 1 by ~5 weeks — scope and contract risk compounding.',
  bottomLines: [
    'Two teams are building against a launch date that already moved — wasted work is piling up.',
    'An approved architecture decision never reached the team implementing it.',
    'The recovery plan buys back four weeks, but needs a scope call this week.',
  ],
  verdict: 'off-track',
  targetDate: 'Dec 1',
  projectedDate: 'Jan 5',
  slipLabel: '+35 days',
  confidenceNote:
    'High confidence — the gap has widened for three sprints straight.',
  progressPct: 41,
  stats: [
    { value: '41', unit: '%', label: 'scope complete' },
    { value: '+35', unit: 'days', label: 'projected slip', isUrgent: true },
    { value: '4', label: 'commitments breaking', isUrgent: true },
    { value: '2', label: 'teams off-plan', isUrgent: true },
    { value: '12', label: 'engineers on it' },
  ],
  timeline: {
    axis: ['Oct', 'Nov', 'Dec', 'Jan'],
    committedPosition: 58,
    projectedPosition: 92,
    milestones: [
      {
        id: 'kickoff',
        label: 'Kickoff',
        dateLabel: 'Oct 1',
        status: 'done',
        position: 2,
      },
      {
        id: 'event-model',
        label: 'Event model',
        dateLabel: 'Oct 24',
        status: 'done',
        position: 24,
      },
      {
        id: 'dual-write',
        label: 'Dual-write live',
        dateLabel: 'Nov 14',
        status: 'at-risk',
        position: 44,
      },
      {
        id: 'cutover',
        label: 'Cutover',
        dateLabel: 'Dec 1',
        status: 'at-risk',
        position: 58,
      },
      {
        id: 'decommission',
        label: 'Legacy off',
        dateLabel: 'Jan 5',
        status: 'upcoming',
        position: 92,
      },
    ],
  },
  remediation: {
    headline: 'Four moves pull the date back to early December.',
    blurb:
      'Most of the slip is rework from a decision that never propagated. Re-align the two teams and cut one module to recover four weeks.',
    recoversLabel: '+28 days',
    steps: [
      {
        id: 'propagate-adr',
        title: 'Push ADR-31 to the team building against the old design',
        detail:
          'An approved ADR never reached the implementers; they are coding against the deprecated SQS client.',
        impactLabel: 'stops +8 days of rework',
        actionLabel: 'Brief the team',
      },
      {
        id: 'realign-gtm',
        title: 'Re-plan the sprint against the moved launch date',
        detail:
          'GTM moved the date two weeks ago; the eng sprint never changed. Re-prioritize to the real date.',
        impactLabel: 'recovers +10 days',
        actionLabel: 'Re-prioritize',
      },
      {
        id: 'defer-module',
        title: 'Defer reporting cutover to a fast-follow',
        detail:
          'The reporting cutover is blocking the core ledger path. Split it out to unblock the critical line.',
        impactLabel: 'recovers +10 days',
        actionLabel: 'Split scope',
      },
    ],
  },
  procedures: [
    // Off-book, but already surfaced live in "Breaking now" - de-duped out.
    {
      id: 'lm-adr-rule',
      name: 'Approved decisions reach the implementing team',
      summary:
        'An approved ADR must be referenced in the tickets of the team building against it.',
      isFollowed: false,
      complianceLabel: '0 of 1 decisions',
      compliancePct: 0,
      trendLabel: 'breaking now',
      severity: 'high',
      relatedBreakId: 'arch-decision-not-propagated',
    },
    {
      id: 'lm-loop-rule',
      name: 'Date changes pull the affected team in same-day',
      summary:
        'When a launch date moves, the team building against it is pulled in the same day.',
      isFollowed: false,
      complianceLabel: '0 of 1 changes',
      compliancePct: 0,
      trendLabel: 'breaking now',
      severity: 'high',
      relatedBreakId: 'gtm-shift-sprint-unchanged',
    },
    {
      id: 'lm-contract-rule',
      name: 'Endpoints match the documented API contract',
      summary:
        'Shipped endpoints are validated against the published contract on merge.',
      isFollowed: false,
      complianceLabel: '2 drifts this month',
      compliancePct: 55,
      trendLabel: 'breaking now',
      severity: 'medium',
      relatedBreakId: 'api-contract-drift',
    },
    // Silent off-book drift - not yet a live incident, so it shows in the column.
    {
      id: 'lm-parity-rule',
      name: 'Dual-write parity is verified before cutover',
      summary:
        'Each table’s dual-write output is reconciled against the legacy ledger before cutover.',
      isFollowed: false,
      complianceLabel: '5 of 8 tables',
      compliancePct: 63,
      trendLabel: 'drifting 2 sprints',
      severity: 'high',
    },
    {
      id: 'lm-runbook-rule',
      name: 'Each cutover step has a runbook',
      summary:
        'Every cutover step is documented with a runbook before it can be scheduled.',
      isFollowed: false,
      complianceLabel: '5 of 9 steps',
      compliancePct: 56,
      trendLabel: 'stalled 2 sprints',
      severity: 'medium',
    },
    // Followed - held over time.
    {
      id: 'lm-rollback-rule',
      name: 'Migrations ship with a rollback plan',
      summary:
        'Every migration carries a documented, tested rollback before review.',
      isFollowed: true,
      complianceLabel: '4 of 4 migrations',
      compliancePct: 100,
      trendLabel: 'held all quarter',
    },
    {
      id: 'lm-coverage-rule',
      name: 'PRs carry tests above the 85% line',
      summary: 'Merged PRs keep coverage above the team’s 85% floor.',
      isFollowed: true,
      complianceLabel: '87% coverage',
      compliancePct: 87,
      trendLabel: 'above the line all sprint',
    },
  ],
  breakingCommitments: [
    {
      id: 'risk-664',
      breakId: 'arch-decision-not-propagated',
      severity: 'high',
      tag: 'Decision not propagated',
      ref: 'RISK-664',
      ageLabel: '1d ago',
      title: 'An approved ADR never reached the team building against it',
      detail:
        'The team is implementing against the deprecated SQS queue client; no ticket references ADR-31.',
      currentMetric: '+8 days',
      targetMetric: 'of rework so far',
      platforms: ['confluence', 'jira'],
      owner: { name: 'Tom Baker', initials: 'TB' },
    },
    {
      id: 'grow-203',
      breakId: 'gtm-shift-sprint-unchanged',
      severity: 'high',
      tag: 'Team not looped in',
      ref: 'GROW-203',
      ageLabel: '6h ago',
      title: 'Launch moved two weeks — the eng sprint never changed',
      detail:
        'GTM delayed the launch; two tickets are still being built against the original date.',
      currentMetric: '2 tickets',
      targetMetric: 'on a stale date',
      platforms: ['slack', 'jira'],
      owner: { name: 'Lena Ortiz', initials: 'LO' },
    },
    {
      id: 'orders-5102',
      breakId: 'api-contract-drift',
      severity: 'medium',
      tag: 'Contract drift',
      ref: 'PR #5102',
      ageLabel: '8h ago',
      title: 'Ledger read endpoint drifted from the documented contract',
      detail:
        'The endpoint nests customer fields the published contract does not declare — the second drift this month.',
      currentMetric: '2nd drift',
      targetMetric: 'this month',
      platforms: ['github', 'confluence'],
      owner: { name: 'Priya Shah', initials: 'PS' },
    },
    {
      id: 'data-842',
      breakId: 'data-pipeline-reestimated',
      severity: 'medium',
      tag: 'Re-estimate, no notice',
      ref: 'DATA-842',
      ageLabel: '2h ago',
      title: 'Shared pipeline re-estimated 3×, reporting team never told',
      detail:
        'The ingestion ticket slipped to Sprint 44; the dependent reporting epic still assumes Sprint 42.',
      currentMetric: '3 re-points',
      targetMetric: 'no notice sent',
      platforms: ['jira', 'slack'],
      owner: { name: 'Sarah Chen', initials: 'SC' },
    },
  ],
};

/** All demo projects, in the order the switcher shows them. */
export const PROJECTS: ProjectOverview[] = [
  CHECKOUT_REVAMP_PROJECT,
  FRAUD_SIGNALS_PROJECT,
  LEDGER_MIGRATION_PROJECT,
];

/** The project shown first, and the fallback when an id doesn't match. */
export const DEFAULT_PROJECT: ProjectOverview = CHECKOUT_REVAMP_PROJECT;

/** Look up a project by id, falling back to the default. */
export function getProjectById(projectId: string): ProjectOverview {
  return (
    PROJECTS.find((project) => project.id === projectId) ?? DEFAULT_PROJECT
  );
}
